/**
 * Fret-diatonic-minor recognition — guitar rung 7.
 *
 * A i–iv–V–i cadence establishes a minor key (harmonic minor: V is major).
 * Then a diatonic chord (i, ♭III, iv, V, or ♭VII) plays. The CAGED shape
 * region is shown with the non-root chord tones as `given`; the user taps
 * the root on the canonical string.
 *
 * Same single-tap auto-submit mechanic as rung 6. The new cognitive demand:
 * minor key context, and the V chord is major (harmonic minor), making the
 * tonic–dominant relationship different from natural minor.
 *
 * See docs/design/fret-diatonic-triads.md for the rung 6 pattern this follows.
 */

import { AudioPlanBuilder, type AudioPlan } from "./audio-plan.js";
import { Rng, type Exercise, type FretboardPrompt } from "./exercise.js";
import { buildChord, smoothVoicing, type CadenceOptions } from "./utils.js";
import {
  STANDARD_TUNING,
  CAGED_SHAPES,
  midiAt,
  type FretPosition,
  type CagedShape,
  type CagedVoice,
} from "@music-app/theory-engine";
import { resolveVoiceMidi } from "./exercise-fret-caged.js";

// ─── Degree definitions ────────────────────────────────────────────────────────

export type MinorDiatonicDegree = "i" | "III" | "iv" | "V" | "VII";

/** Semitones above the minor-key tonic for each diatonic degree. */
const MINOR_DEGREE_SEMITONES: Record<MinorDiatonicDegree, number> = {
  i:   0,
  III: 3,   // ♭III
  iv:  5,
  V:   7,   // dominant — harmonic minor raised leading tone makes this major
  VII: 10,  // ♭VII
};

/** Triad quality for each diatonic degree in minor (harmonic minor V). */
const MINOR_DEGREE_QUALITY: Record<MinorDiatonicDegree, "maj" | "min"> = {
  i:   "min",
  III: "maj",
  iv:  "min",
  V:   "maj",
  VII: "maj",
};

// ─── Param type ───────────────────────────────────────────────────────────────

export interface FretDiatonicMinorParams {
  shape: CagedShape;
  degree: MinorDiatonicDegree;
}

// ─── Key helpers ──────────────────────────────────────────────────────────────

const PC_NAMES = ["C","D♭","D","E♭","E","F","F♯","G","A♭","A","B♭","B"] as const;

interface MinorKeyChoice {
  name: string;
  tonicMidi: number;
  letter: string;
}

function buildMinorKey(tonicMidi: number): MinorKeyChoice {
  const pc = ((tonicMidi % 12) + 12) % 12;
  const pcName = PC_NAMES[pc]!;
  const letter = pcName.replace(/[♭♯]/u, "");
  const accSym = pcName.includes("♭") ? "♭" : pcName.includes("♯") ? "♯" : "";
  return {
    letter,
    tonicMidi: 60 + pc,
    name: `${letter}${accSym} minor`,
  };
}

// ─── Harmonic minor cadence ───────────────────────────────────────────────────

/**
 * Append a i–iv–V–i cadence to a builder in harmonic minor.
 * The V chord is major (raised leading tone), unlike natural-minor where V is minor.
 */
function appendHarmonicMinorCadence(
  builder: AudioPlanBuilder,
  tonicMidi: number,
  opts: CadenceOptions = {},
): void {
  const dur = opts.chordDuration ?? 0.85;
  const gap = opts.gap ?? 0.05;
  const gain = opts.gain ?? 0.07;
  const bassGain = opts.bassGain ?? 0.11;

  // i–iv–V–i; V is explicitly major (harmonic minor).
  const degrees: Array<{ offset: number; quality: "maj" | "min" }> = [
    { offset: 0, quality: "min" }, // i
    { offset: 5, quality: "min" }, // iv
    { offset: 7, quality: "maj" }, // V (harmonic minor)
    { offset: 0, quality: "min" }, // i
  ];

  const normalTonicMidi = 60 + ((tonicMidi % 12) + 12) % 12;
  let prevVoicing: number[] | null = null;

  for (const { offset, quality } of degrees) {
    const rootMidi = normalTonicMidi + offset;
    const triad = buildChord(rootMidi, quality);
    const voicing = smoothVoicing(prevVoicing, triad);
    prevVoicing = voicing;
    builder.addNote(rootMidi - 12, dur, bassGain, "bass");
    builder.addChord(voicing, dur, gain, "harmony");
    builder.advance(dur + gap);
  }
  builder.advance(0.25);
}

// ─── Public generator ─────────────────────────────────────────────────────────

export function generateFretDiatonicMinor(
  itemId: string,
  params: FretDiatonicMinorParams,
  seed: number,
  cadenceOpts?: CadenceOptions,
): Exercise {
  const rng = new Rng(seed);

  const shapeDef = CAGED_SHAPES[params.shape];
  if (!shapeDef) {
    throw new Error(`CAGED shape not implemented: ${params.shape}`);
  }

  const { canonicalRootString, pattern } = shapeDef;
  const minRootFret = shapeDef.minRootFret ?? 2;
  const quality = MINOR_DEGREE_QUALITY[params.degree];
  const semitones = MINOR_DEGREE_SEMITONES[params.degree];

  // Root fret for the diatonic chord in [minRootFret, 12].
  const rootFret = minRootFret + rng.int(12 - minRootFret + 1);
  const diatonicRootMidi = midiAt(STANDARD_TUNING, { stringIndex: canonicalRootString, fret: rootFret });

  // Back-compute the minor tonic.
  const tonicMidi = diatonicRootMidi - semitones;
  const key = buildMinorKey(tonicMidi);

  // Chord audio shifted up one octave for clear mid-range sound.
  const chordAudioRoot = diatonicRootMidi + 12;
  const chordMidis = buildChord(chordAudioRoot, quality);
  const [chordRoot, chordThird, chordFifth] = chordMidis as [number, number, number];

  // First pass: resolve all voice positions.
  interface VoicePos { stringIndex: number; fret: number; voice: CagedVoice; }
  const voicePositions: VoicePos[] = [];
  for (const [stringIndex, voice] of pattern) {
    const targetMidi = resolveVoiceMidi(diatonicRootMidi, voice, quality);
    const openMidi = midiAt(STANDARD_TUNING, { stringIndex, fret: 0 });
    const fret = targetMidi - openMidi;
    if (fret < 0 || fret > STANDARD_TUNING.fretCount) continue;
    voicePositions.push({ stringIndex, fret, voice });
  }

  // Dynamic fret window: shape extent ±1 fret.
  let minFret = rootFret;
  let maxFret = rootFret;
  for (const vp of voicePositions) {
    minFret = Math.min(minFret, vp.fret);
    maxFret = Math.max(maxFret, vp.fret);
  }
  const fretWindow = {
    from: Math.max(0, minFret - 1),
    to: Math.min(STANDARD_TUNING.fretCount, maxFret + 1),
  };

  // Second pass: partition voices.
  const given: FretPosition[] = [];
  const acceptedPositions: FretPosition[] = [];
  const solution: FretPosition[] = [];

  for (const { stringIndex, fret, voice } of voicePositions) {
    const pos: FretPosition = { stringIndex, fret };
    solution.push(pos);
    if (voice.role === "root" && stringIndex === canonicalRootString) {
      acceptedPositions.push(pos);
    } else if (voice.role !== "root") {
      given.push(pos);
    }
  }

  const fretboard: FretboardPrompt = {
    tuning: "standard",
    given,
    allowedStrings: [canonicalRootString],
    notesToPlace: 1,
    acceptedPositions,
    solution,
    listenFretWindow: fretWindow,
  };

  // Audio: harmonic minor cadence → diatonic chord arpeggio → block chord.
  const builder = new AudioPlanBuilder();
  appendHarmonicMinorCadence(builder, tonicMidi, cadenceOpts);
  for (const note of [chordRoot, chordThird, chordFifth]) {
    builder.addNote(note, 0.55, 0.2, "melody");
    builder.advance(0.3);
  }
  builder.advance(0.4);
  builder.addChord([chordRoot, chordThird, chordFifth], 1.4, 0.13, "harmony");
  builder.advance(1.5);
  const audio = builder.build();

  const chordPcName = PC_NAMES[((diatonicRootMidi % 12) + 12) % 12]!;
  const qualityLabel = quality === "maj" ? "major" : "minor";
  const degreeDisplay = params.degree === "i" ? "i" :
    params.degree === "III" ? "♭III" :
    params.degree === "iv" ? "iv" :
    params.degree === "V" ? "V" : "♭VII";

  return {
    itemId,
    exerciseType: "fret-diatonic-minor-recognition",
    audio,
    prompt: `Tap the ${params.degree} root`,
    options: [],
    correctAnswerId: "",
    meta: {
      keyName: key.name,
      seed,
      explanation: `${chordPcName} ${qualityLabel} — ${params.shape}-shape, degree ${degreeDisplay} in ${key.name}.`,
      distractorCount: 0,
      tonicMidi: diatonicRootMidi,
      tonalityMode: quality === "maj" ? "major" : "minor",
      teachAudio: buildMinorDiatonicTeachPlan(chordRoot, chordThird, chordFifth),
      fretboard,
      playbackMidis: [chordRoot, chordThird, chordFifth],
    },
  };
}

function buildMinorDiatonicTeachPlan(rootMidi: number, thirdMidi: number, fifthMidi: number): AudioPlan {
  const builder = new AudioPlanBuilder();
  for (const note of [rootMidi, thirdMidi, fifthMidi]) {
    builder.addNote(note, 0.8, 0.22, "melody");
    builder.advance(0.4);
  }
  builder.advance(0.3);
  builder.addChord([rootMidi, thirdMidi, fifthMidi], 1.5, 0.13, "harmony");
  builder.advance(1.6);
  return builder.build();
}
